import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  TenantApiError,
  type TenantTeamInvitation,
  type TenantTeamMember,
} from "@qr-menu/api-tenant";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  RowActions,
  RowConfirm,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@qr-menu/ui";
import { tenantApi } from "../lib/api";

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "ready";
      members: TenantTeamMember[];
      invitations: TenantTeamInvitation[];
    };

const ROLE_LABELS: Record<string, string> = {
  owner: "Sahip",
  admin: "Yönetici",
  editor: "Editör",
};

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role;
}

function describe(err: unknown, fallback: string): string {
  // TenantApiError.message carries the backend's curated public error_message,
  // never a raw cause or response body.
  if (err instanceof TenantApiError) {
    return err.message;
  }
  return fallback;
}

export function TeamRoute() {
  const [state, setState] = useState<State>({ kind: "loading" });

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"admin" | "editor">("editor");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const [resendingId, setResendingId] = useState<number | null>(null);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const [pendingRevokeId, setPendingRevokeId] = useState<number | null>(null);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const [updatingRoleId, setUpdatingRoleId] = useState<number | null>(null);
  const [roleUpdateMessage, setRoleUpdateMessage] = useState<string | null>(
    null,
  );
  const [roleUpdateError, setRoleUpdateError] = useState<string | null>(null);

  const [pendingRemoveMembershipId, setPendingRemoveMembershipId] = useState<
    number | null
  >(null);
  const [removingMembershipId, setRemovingMembershipId] = useState<
    number | null
  >(null);
  const [removeMemberError, setRemoveMemberError] = useState<string | null>(
    null,
  );

  // One in-flight mutation at a time, the same shape as Categories.tsx.
  const busy =
    inviteSubmitting ||
    resendingId !== null ||
    revokingId !== null ||
    updatingRoleId !== null ||
    removingMembershipId !== null;

  const load = useCallback(async () => {
    try {
      const [members, invitations] = await Promise.all([
        tenantApi.teamMembers(),
        tenantApi.teamInvitations(),
      ]);
      setState({ kind: "ready", members, invitations });
    } catch (err) {
      setState({
        kind: "error",
        message: describe(err, "Ekip bilgileri alınamadı."),
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleInviteSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;

    const trimmedEmail = inviteEmail.trim();
    if (trimmedEmail === "") {
      setInviteError("E-posta zorunludur.");
      return;
    }

    setInviteSubmitting(true);
    setInviteError(null);
    setInviteSuccess(null);
    setResendMessage(null);
    try {
      await tenantApi.createInvitation({
        email: trimmedEmail,
        role: inviteRole,
      });
      setInviteEmail("");
      setInviteRole("editor");
      await load();
      setInviteSuccess("Davet gönderildi.");
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setInviteError(describe(err, "Davet gönderilemedi."));
      }
    } finally {
      setInviteSubmitting(false);
    }
  }

  async function handleResend(id: number) {
    if (busy) return;

    setResendingId(id);
    setResendError(null);
    setResendMessage(null);
    try {
      await tenantApi.resendInvitation(id);
      await load();
      setResendMessage("Davet yeniden gönderildi.");
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setResendError(describe(err, "Davet yeniden gönderilemedi."));
      }
    } finally {
      setResendingId(null);
    }
  }

  function handleRevokeRequest(id: number) {
    setPendingRevokeId(id);
    setRevokeError(null);
  }

  function handleRevokeCancel() {
    setPendingRevokeId(null);
    setRevokeError(null);
  }

  async function handleRevokeConfirm(id: number) {
    if (busy) return;

    setRevokingId(id);
    setRevokeError(null);
    try {
      await tenantApi.revokeInvitation(id);
      setPendingRevokeId(null);
      await load();
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setRevokeError(describe(err, "Davet iptal edilemedi."));
      }
    } finally {
      setRevokingId(null);
    }
  }

  async function handleUpdateRole(membershipId: number, role: "admin" | "editor") {
    if (busy) return;

    setUpdatingRoleId(membershipId);
    setRoleUpdateError(null);
    setRoleUpdateMessage(null);
    try {
      await tenantApi.updateTeamMemberRole(membershipId, { role });
      await load();
      setRoleUpdateMessage("Rol güncellendi.");
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setRoleUpdateError(describe(err, "Rol güncellenemedi."));
      }
    } finally {
      setUpdatingRoleId(null);
    }
  }

  function handleRemoveMemberRequest(membershipId: number) {
    setPendingRemoveMembershipId(membershipId);
    setRemoveMemberError(null);
  }

  function handleRemoveMemberCancel() {
    setPendingRemoveMembershipId(null);
    setRemoveMemberError(null);
  }

  async function handleRemoveMemberConfirm(membershipId: number) {
    if (busy) return;

    setRemovingMembershipId(membershipId);
    setRemoveMemberError(null);
    try {
      await tenantApi.removeTeamMember(membershipId);
      setPendingRemoveMembershipId(null);
      await load();
    } catch (err) {
      if (!(err instanceof TenantApiError && err.status === 401)) {
        setRemoveMemberError(describe(err, "Üye çıkarılamadı."));
      }
    } finally {
      setRemovingMembershipId(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ekip</CardTitle>
        <CardDescription>
          İşletmenizin ekip üyeleri ve bekleyen davetleri.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form className="create-form" onSubmit={handleInviteSubmit}>
          <p className="form-heading">Ekibe davet gönder</p>
          <div className="create-form-grid">
            <Field label="E-posta" htmlFor="team-invite-email">
              <input
                id="team-invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="ornek@isletme.com"
              />
            </Field>
            <Field label="Rol" htmlFor="team-invite-role">
              <select
                id="team-invite-role"
                value={inviteRole}
                onChange={(e) =>
                  setInviteRole(e.target.value as "admin" | "editor")
                }
              >
                <option value="editor">Editör</option>
                <option value="admin">Yönetici</option>
              </select>
            </Field>
            <div className="filter-actions">
              <button type="submit" className="primary" disabled={busy}>
                {inviteSubmitting ? "Gönderiliyor…" : "Davet gönder"}
              </button>
            </div>
          </div>
          {inviteError && <div className="state error">{inviteError}</div>}
          {inviteSuccess && <div className="state ok">{inviteSuccess}</div>}
        </form>

        {state.kind === "loading" && <div className="state">Yükleniyor…</div>}

        {state.kind === "error" && (
          <div className="state error">Yüklenemedi. {state.message}</div>
        )}

        {state.kind === "ready" && (
          <>
            <h3>Ekip üyeleri</h3>
            {roleUpdateMessage && (
              <div className="state ok">{roleUpdateMessage}</div>
            )}
            {roleUpdateError && (
              <div className="state error">{roleUpdateError}</div>
            )}
            {removeMemberError && (
              <div className="state error">{removeMemberError}</div>
            )}
            <MembersTable
              members={state.members}
              actionsDisabled={busy}
              updatingRoleId={updatingRoleId}
              pendingRemoveId={pendingRemoveMembershipId}
              removingId={removingMembershipId}
              onUpdateRole={handleUpdateRole}
              onRemoveRequest={handleRemoveMemberRequest}
              onRemoveConfirm={handleRemoveMemberConfirm}
              onRemoveCancel={handleRemoveMemberCancel}
            />

            <h3 style={{ marginTop: 32 }}>Bekleyen davetler</h3>
            {resendMessage && <div className="state ok">{resendMessage}</div>}
            {resendError && <div className="state error">{resendError}</div>}
            {revokeError && <div className="state error">{revokeError}</div>}
            <InvitationsTable
              invitations={state.invitations}
              actionsDisabled={busy}
              resendingId={resendingId}
              pendingRevokeId={pendingRevokeId}
              revokingId={revokingId}
              onResend={handleResend}
              onRevokeRequest={handleRevokeRequest}
              onRevokeConfirm={handleRevokeConfirm}
              onRevokeCancel={handleRevokeCancel}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

interface MembersTableProps {
  members: TenantTeamMember[];
  actionsDisabled: boolean;
  updatingRoleId: number | null;
  pendingRemoveId: number | null;
  removingId: number | null;
  onUpdateRole: (membershipId: number, role: "admin" | "editor") => void;
  onRemoveRequest: (membershipId: number) => void;
  onRemoveConfirm: (membershipId: number) => void;
  onRemoveCancel: () => void;
}

function MembersTable({
  members,
  actionsDisabled,
  updatingRoleId,
  pendingRemoveId,
  removingId,
  onUpdateRole,
  onRemoveRequest,
  onRemoveConfirm,
  onRemoveCancel,
}: MembersTableProps) {
  if (members.length === 0) {
    return <div className="state">Henüz ekip üyesi bulunmuyor.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableCell as="th">Ad Soyad</TableCell>
          <TableCell as="th">E-posta</TableCell>
          <TableCell as="th" style={{ width: 140 }}>
            Rol
          </TableCell>
          <TableCell as="th" style={{ width: 260 }}>
            İşlem
          </TableCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            actionsDisabled={actionsDisabled}
            updating={updatingRoleId === member.id}
            pendingRemove={pendingRemoveId === member.id}
            removing={removingId === member.id}
            onUpdateRole={onUpdateRole}
            onRemoveRequest={onRemoveRequest}
            onRemoveConfirm={onRemoveConfirm}
            onRemoveCancel={onRemoveCancel}
          />
        ))}
      </TableBody>
    </Table>
  );
}

interface MemberRowProps {
  member: TenantTeamMember;
  actionsDisabled: boolean;
  updating: boolean;
  pendingRemove: boolean;
  removing: boolean;
  onUpdateRole: (membershipId: number, role: "admin" | "editor") => void;
  onRemoveRequest: (membershipId: number) => void;
  onRemoveConfirm: (membershipId: number) => void;
  onRemoveCancel: () => void;
}

// Owner protection is a backend rule (a mutation targeting an owner
// membership is rejected regardless of what id is sent); hiding the
// controls here is UX only, not the authority.
function MemberRow({
  member,
  actionsDisabled,
  updating,
  pendingRemove,
  removing,
  onUpdateRole,
  onRemoveRequest,
  onRemoveConfirm,
  onRemoveCancel,
}: MemberRowProps) {
  const [draftRole, setDraftRole] = useState<"admin" | "editor">(
    member.role === "admin" ? "admin" : "editor",
  );

  // member.role is the DB-authoritative value; a reload triggered by an
  // unrelated row's mutation must not leave this row's selector showing a
  // stale draft that no longer matches the server.
  useEffect(() => {
    setDraftRole(member.role === "admin" ? "admin" : "editor");
  }, [member.role]);

  if (member.role === "owner") {
    return (
      <TableRow>
        <TableCell className="wrap-cell">
          {member.name} {member.surname}
        </TableCell>
        <TableCell className="wrap-cell">{member.email}</TableCell>
        <TableCell>
          <Badge>Sahip</Badge>
        </TableCell>
        <RowActions as="td" />
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="wrap-cell">
        {member.name} {member.surname}
      </TableCell>
      <TableCell className="wrap-cell">{member.email}</TableCell>
      <TableCell>
        {pendingRemove ? (
          <Badge>{roleLabel(member.role)}</Badge>
        ) : (
          <select
            value={draftRole}
            onChange={(e) =>
              setDraftRole(e.target.value as "admin" | "editor")
            }
            disabled={actionsDisabled}
          >
            <option value="editor">Editör</option>
            <option value="admin">Yönetici</option>
          </select>
        )}
      </TableCell>
      <RowActions as="td">
        {pendingRemove ? (
          <RowConfirm>
            <span className="hint">Üye ekipten çıkarılsın mı?</span>
            <Button
              type="button"
              variant="danger"
              onClick={() => onRemoveConfirm(member.id)}
              disabled={actionsDisabled}
            >
              {removing ? "Çıkarılıyor…" : "Evet, çıkar"}
            </Button>
            <Button
              type="button"
              onClick={onRemoveCancel}
              disabled={actionsDisabled}
            >
              Vazgeç
            </Button>
          </RowConfirm>
        ) : (
          <RowConfirm>
            <Button
              type="button"
              onClick={() => onUpdateRole(member.id, draftRole)}
              disabled={actionsDisabled || draftRole === member.role}
            >
              {updating ? "Güncelleniyor…" : "Rolü güncelle"}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => onRemoveRequest(member.id)}
              disabled={actionsDisabled}
            >
              Üyeyi çıkar
            </Button>
          </RowConfirm>
        )}
      </RowActions>
    </TableRow>
  );
}

interface InvitationsTableProps {
  invitations: TenantTeamInvitation[];
  actionsDisabled: boolean;
  resendingId: number | null;
  pendingRevokeId: number | null;
  revokingId: number | null;
  onResend: (id: number) => void;
  onRevokeRequest: (id: number) => void;
  onRevokeConfirm: (id: number) => void;
  onRevokeCancel: () => void;
}

function InvitationsTable({
  invitations,
  actionsDisabled,
  resendingId,
  pendingRevokeId,
  revokingId,
  onResend,
  onRevokeRequest,
  onRevokeConfirm,
  onRevokeCancel,
}: InvitationsTableProps) {
  if (invitations.length === 0) {
    return <div className="state">Bekleyen davet bulunmuyor.</div>;
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableCell as="th">E-posta</TableCell>
          <TableCell as="th" style={{ width: 120 }}>
            Rol
          </TableCell>
          <TableCell as="th" style={{ width: 100 }}>
            Durum
          </TableCell>
          <TableCell as="th" style={{ width: 220 }}>
            İşlem
          </TableCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invitations.map((invitation) => (
          <TableRow key={invitation.id}>
            <TableCell className="wrap-cell">{invitation.email}</TableCell>
            <TableCell>
              <Badge>{roleLabel(invitation.role)}</Badge>
            </TableCell>
            <TableCell>
              <Badge
                variant={
                  invitation.status === "pending" ? "success" : "danger"
                }
              >
                {invitation.status === "pending" ? "Bekliyor" : "Süresi doldu"}
              </Badge>
            </TableCell>
            <RowActions as="td">
              {pendingRevokeId === invitation.id ? (
                <RowConfirm>
                  <span className="hint">İptal edilsin mi?</span>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => onRevokeConfirm(invitation.id)}
                    disabled={actionsDisabled}
                  >
                    {revokingId === invitation.id
                      ? "İptal ediliyor…"
                      : "Evet, iptal et"}
                  </Button>
                  <Button
                    type="button"
                    onClick={onRevokeCancel}
                    disabled={actionsDisabled}
                  >
                    Vazgeç
                  </Button>
                </RowConfirm>
              ) : (
                <RowConfirm>
                  <Button
                    type="button"
                    onClick={() => onResend(invitation.id)}
                    disabled={actionsDisabled}
                  >
                    {resendingId === invitation.id
                      ? "Gönderiliyor…"
                      : "Yeniden gönder"}
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => onRevokeRequest(invitation.id)}
                    disabled={actionsDisabled}
                  >
                    İptal et
                  </Button>
                </RowConfirm>
              )}
            </RowActions>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
